import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import API from '../../services/API'
import { DataGrid, GridToolbarQuickFilter } from '@mui/x-data-grid'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import PhonelinkEraseIcon from '@mui/icons-material/PhonelinkErase'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

// Demo visitor accounts for the demo tenant (registry-DB DemoUsers, see
// docs/product-specs/demo-tenant-design.md). Each prospect gets their own
// credentials with an optional access window; sessions can be killed
// immediately from here.

const statusChip = (row) => {
  if (row.isActive === false)
    return { label: 'Disabled', color: 'default', variant: 'outlined' }
  if (row.accessExpiresAt && moment(row.accessExpiresAt).isBefore(moment()))
    return { label: 'Expired', color: 'anRed' }
  if (row.accessExpiresAt)
    return {
      label: `Until ${moment(row.accessExpiresAt).format('Do MMM YYYY')}`,
      color: 'anGreen',
    }
  return { label: 'Active', color: 'anGreen' }
}

const lastActivityColour = (value) => {
  if (!value) return 'text.disabled'
  const daysAgo = moment().diff(moment(value), 'days')
  if (daysAgo <= 2) return 'anGreen.main'
  if (daysAgo <= 7) return 'anOrange.main'
  return 'anRed.main'
}

const Toolbar = () => (
  <Stack direction="row" justifyContent="flex-end" padding={1}>
    <GridToolbarQuickFilter />
  </Stack>
)

const emptyForm = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  company: '',
  notes: '',
  accessExpiresAt: '',
  isActive: true,
  trainingAssessor: false,
}

const DemoUsers = () => {
  const navigate = useNavigate()
  const [demoUsers, setDemoUsers] = useState([])
  const [loading, setLoading] = useState(true)

  // dialog = { mode: 'create' } | { mode: 'edit', id }
  const [dialog, setDialog] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [dialogError, setDialogError] = useState('')
  const [resetting, setResetting] = useState(false)

  const loadDemoUsers = () => {
    API.get('/admin/demo-users')
      .then((res) => {
        if (res.status === 200) {
          setDemoUsers(res.data)
        }
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadDemoUsers()
  }, [])

  const openCreate = () => {
    setForm(emptyForm)
    setDialogError('')
    setDialog({ mode: 'create' })
  }

  const openEdit = (row) => {
    setForm({
      firstName: row.firstName ?? '',
      lastName: row.lastName ?? '',
      email: row.email ?? '',
      password: '',
      company: row.company ?? '',
      notes: row.notes ?? '',
      accessExpiresAt: row.accessExpiresAt
        ? moment(row.accessExpiresAt).format('YYYY-MM-DD')
        : '',
      isActive: row.isActive !== false,
      trainingAssessor: row.trainingAssessor === true,
    })
    setDialogError('')
    setDialog({ mode: 'edit', id: row._id })
  }

  const setField = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }))

  const handleSave = async () => {
    setDialogError('')

    if (!form.email) {
      setDialogError('An email address is required.')
      return
    }
    if (dialog.mode === 'create' && form.password.length < 8) {
      setDialogError('The password must be at least 8 characters.')
      return
    }
    if (dialog.mode === 'edit' && form.password && form.password.length < 8) {
      setDialogError('The new password must be at least 8 characters.')
      return
    }

    const accessExpiresAt = form.accessExpiresAt
      ? moment(form.accessExpiresAt, 'YYYY-MM-DD').endOf('day').toISOString()
      : null

    const body = {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      company: form.company,
      notes: form.notes,
      accessExpiresAt,
      trainingAssessor: form.trainingAssessor,
    }

    setSaving(true)

    let res
    if (dialog.mode === 'create') {
      res = await API.post('/admin/demo-users', {
        ...body,
        password: form.password,
      })
    } else {
      res = await API.patch(`/admin/demo-users/${dialog.id}`, {
        ...body,
        isActive: form.isActive,
        ...(form.password ? { password: form.password } : {}),
      })
    }

    setSaving(false)

    if (res.status !== 200 && res.status !== 201) {
      setDialogError(res.data?.error ?? 'Failed to save the demo user')
      return
    }

    setDialog(null)
    loadDemoUsers()
  }

  const handleKillSessions = async (row) => {
    if (
      !window.confirm(
        `Log ${row.firstName || row.email} out of all their demo sessions?`,
      )
    )
      return

    const res = await API.post(`/admin/demo-users/${row._id}/kill-sessions`)
    if (res.status !== 200) {
      window.alert(res.data?.error ?? 'Failed to kill sessions')
      return
    }
    window.alert(
      `Killed ${res.data?.killed ?? 0} active session${
        res.data?.killed === 1 ? '' : 's'
      }.`,
    )
  }

  // Same code path as the 04:30 DemoResetScheduler run. The seed is validated
  // before anything is wiped, so a failure leaves the current demo data intact.
  const handleResetDemo = async () => {
    if (
      !window.confirm(
        'Reset the demo now? All demo data is wiped and reloaded from the seed — anyone currently browsing the demo will see their changes disappear.',
      )
    )
      return

    setResetting(true)
    const res = await API.post('/admin/demo/reset')
    setResetting(false)

    if (res.status !== 200) {
      window.alert(
        `Demo reset failed — the existing demo data has been left as it was.\n\n${
          res.data?.error ?? 'Unknown error'
        }`,
      )
      return
    }
    const results = res.data?.results ?? []
    if (!results.length) {
      window.alert('No active demo tenants were found to reset.')
      return
    }
    window.alert(
      results
        .map(
          (r) =>
            `${r.tenant}: ${r.documents} records across ${r.models} models in ${r.seconds}s${
              r.cqcRecomputed ? '' : ' (CQC Centre recompute failed)'
            }`,
        )
        .join('\n'),
    )
  }

  const handleDelete = async (row) => {
    if (
      !window.confirm(
        `Delete the demo user "${row.email}"? Their sessions will be killed and they will no longer be able to log in.`,
      )
    )
      return

    const res = await API.delete(`/admin/demo-users/${row._id}`)
    if (res.status !== 200) {
      window.alert(res.data?.error ?? 'Failed to delete the demo user')
      return
    }
    loadDemoUsers()
  }

  return (
    <Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="title2">Demo Users</Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Wipe and reload the demo tenant from the seed now, instead of waiting for the 04:30 nightly reset">
            <span>
              <Button
                variant="outlined"
                startIcon={<RestartAltIcon />}
                onClick={handleResetDemo}
                disabled={resetting}
              >
                {resetting ? 'Resetting…' : 'Reset Demo Now'}
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
          >
            New Demo User
          </Button>
        </Stack>
      </Stack>
      <DataGrid
        rows={demoUsers}
        loading={loading}
        initialState={{
          sorting: {
            sortModel: [{ field: 'createdAt', sort: 'desc' }],
          },
        }}
        columns={[
          {
            field: 'name',
            headerName: 'Name',
            minWidth: 190,
            flex: 0.8,
            valueGetter: (value, row) =>
              `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim(),
            renderCell: ({ value, row }) => (
              <Stack direction="row" spacing={1} alignItems="center">
                <span>{value}</span>
                {row.trainingAssessor && (
                  <Tooltip title="CPD UK assessor access: training locks off, question bank visible">
                    <Chip
                      label="CPD UK assessor"
                      color="anPurple"
                      size="small"
                      sx={{ borderRadius: 1 }}
                    />
                  </Tooltip>
                )}
              </Stack>
            ),
          },
          { field: 'email', headerName: 'Email', minWidth: 190, flex: 1 },
          {
            field: 'company',
            headerName: 'Company',
            minWidth: 140,
            flex: 0.7,
            renderCell: ({ value }) => value || '—',
          },
          {
            field: 'status',
            headerName: 'Status',
            minWidth: 140,
            flex: 0.6,
            valueGetter: (value, row) => statusChip(row).label,
            renderCell: ({ row }) => {
              const chip = statusChip(row)
              return (
                <Chip
                  label={chip.label}
                  color={chip.color}
                  variant={chip.variant ?? 'filled'}
                  size="small"
                  sx={{ borderRadius: 1 }}
                />
              )
            },
          },
          {
            field: 'disclaimerAcceptedAt',
            headerName: 'Disclaimer',
            minWidth: 120,
            flex: 0.5,
            valueGetter: (value) => (value ? new Date(value) : null),
            sortComparator: (a, b) => (a?.getTime() ?? 0) - (b?.getTime() ?? 0),
            renderCell: ({ value }) =>
              value ? (
                <Chip
                  label={moment(value).format('Do MMM')}
                  color="anGreen"
                  size="small"
                  sx={{ borderRadius: 1 }}
                />
              ) : (
                <Chip
                  label="Not yet"
                  variant="outlined"
                  size="small"
                  sx={{ borderRadius: 1 }}
                />
              ),
          },
          {
            field: 'marketingConsent',
            headerName: 'Marketing Consent',
            minWidth: 120,
            flex: 0.5,
            valueGetter: (value, row) =>
              row.marketingConsent?.agreed ? 'Agreed' : 'No',
            renderCell: ({ row }) =>
              row.marketingConsent?.agreed ? (
                <Tooltip
                  title={
                    row.marketingConsent.agreedAt
                      ? `Agreed ${moment(row.marketingConsent.agreedAt).format(
                          'Do MMM YYYY, HH:mm',
                        )}`
                      : 'Agreed'
                  }
                >
                  <Chip
                    label="Agreed"
                    color="anGreen"
                    size="small"
                    sx={{ borderRadius: 1 }}
                  />
                </Tooltip>
              ) : (
                <Chip
                  label="No"
                  variant="outlined"
                  size="small"
                  sx={{ borderRadius: 1 }}
                />
              ),
          },
          {
            field: 'termsAccepted',
            headerName: 'T&Cs',
            minWidth: 90,
            flex: 0.4,
            valueGetter: (value, row) =>
              row.legal?.acceptedTermsVersion ?? '',
            renderCell: ({ row }) =>
              row.legal?.acceptedTermsVersion ? (
                <Tooltip
                  title={`Accepted Terms v${row.legal.acceptedTermsVersion}`}
                >
                  <Chip
                    label={`v${row.legal.acceptedTermsVersion}`}
                    color="anGreen"
                    size="small"
                    sx={{ borderRadius: 1 }}
                  />
                </Tooltip>
              ) : (
                <Chip
                  label="Not yet"
                  variant="outlined"
                  size="small"
                  sx={{ borderRadius: 1 }}
                />
              ),
          },
          {
            field: 'salesLead',
            headerName: 'Lead',
            minWidth: 100,
            flex: 0.4,
            sortable: false,
            filterable: false,
            renderCell: ({ row }) => {
              const leadId = row.salesLead?._id ?? row.salesLead
              if (!leadId) return '—'
              return (
                <Chip
                  label="View lead"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ borderRadius: 1 }}
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate(`/sales/${leadId}`)
                  }}
                />
              )
            },
          },
          {
            field: 'lastActive',
            headerName: 'Last Active',
            minWidth: 120,
            flex: 0.6,
            valueGetter: (value) => (value ? new Date(value) : null),
            sortComparator: (a, b) => (a?.getTime() ?? 0) - (b?.getTime() ?? 0),
            renderCell: ({ value }) => (
              <Typography
                variant="body2"
                sx={{ color: lastActivityColour(value) }}
              >
                {value ? moment(value).fromNow() : 'Never'}
              </Typography>
            ),
          },
          {
            field: 'createdAt',
            headerName: 'Created',
            minWidth: 140,
            flex: 0.6,
            valueGetter: (value) => (value ? new Date(value) : null),
            sortComparator: (a, b) => (a?.getTime() ?? 0) - (b?.getTime() ?? 0),
            renderCell: ({ value, row }) => (
              <Stack>
                <Typography variant="body2">
                  {value ? moment(value).format('Do MMM YYYY') : '—'}
                </Typography>
                {row.createdBy && (
                  <Typography variant="caption" color="text.secondary">
                    by {row.createdBy.firstName} {row.createdBy.lastName}
                  </Typography>
                )}
              </Stack>
            ),
          },
          {
            field: 'actions',
            headerName: '',
            minWidth: 130,
            sortable: false,
            filterable: false,
            disableExport: true,
            align: 'right',
            renderCell: ({ row }) => (
              <Stack direction="row">
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => openEdit(row)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Kill sessions">
                  <IconButton
                    size="small"
                    onClick={() => handleKillSessions(row)}
                  >
                    <PhonelinkEraseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => handleDelete(row)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ),
          },
        ]}
        getRowId={(row) => row._id}
        disableRowSelectionOnClick
        onRowClick={({ row }) => openEdit(row)}
        slots={{ toolbar: Toolbar }}
        sx={{
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            whiteSpace: 'normal',
            lineHeight: 'normal',
            textAlign: 'center',
          },
          '.MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
          },
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },
          border: 0,
          '& .MuiDataGrid-toolbar': {
            border: 'none',
          },
          '& .MuiDataGrid-main': {
            border: 2,
            borderColor: 'primary.main',
            borderRadius: '0px',
          },
          '& .MuiDataGrid-sortIcon': {
            opacity: 1,
            color: 'primary.contrastText',
          },
          '& .MuiDataGrid-menuIconButton': {
            opacity: 1,
            color: 'primary.contrastText',
          },
          '& .MuiDataGrid-columnHeaders': {
            borderRadius: '0px 0px 0 0',
          },
        }}
      />

      <Dialog
        open={!!dialog}
        onClose={() => setDialog(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {dialog?.mode === 'create' ? 'New Demo User' : 'Edit Demo User'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField
                label="First name"
                value={form.firstName}
                onChange={setField('firstName')}
                fullWidth
              />
              <TextField
                label="Last name"
                value={form.lastName}
                onChange={setField('lastName')}
                fullWidth
              />
            </Stack>
            <TextField
              label="Email address"
              type="email"
              value={form.email}
              onChange={setField('email')}
              fullWidth
              required
            />
            <TextField
              label={
                dialog?.mode === 'create'
                  ? 'Password'
                  : 'New password (leave blank to keep current)'
              }
              type="password"
              value={form.password}
              onChange={setField('password')}
              fullWidth
              required={dialog?.mode === 'create'}
              helperText="Minimum 8 characters"
            />
            <TextField
              label="Company"
              value={form.company}
              onChange={setField('company')}
              fullWidth
            />
            <TextField
              label="Notes"
              value={form.notes}
              onChange={setField('notes')}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Access expires"
              type="date"
              value={form.accessExpiresAt}
              onChange={setField('accessExpiresAt')}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Leave blank for indefinite access. Access ends at the end of the chosen day."
            />
            <Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.trainingAssessor}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        trainingAssessor: e.target.checked,
                      }))
                    }
                  />
                }
                label="CPD UK assessor access (training locks off, question bank visible)"
              />
              <Typography variant="caption" color="text.secondary">
                For The CPD Certification Service's reviewers. Create the
                account with the email assessments@cpduk.co.uk.
              </Typography>
            </Stack>
            {dialog?.mode === 'edit' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        isActive: e.target.checked,
                      }))
                    }
                  />
                }
                label={form.isActive ? 'Enabled' : 'Disabled'}
              />
            )}
            {dialog?.mode === 'edit' && !form.isActive && (
              <Alert severity="warning">
                Disabling this demo user kills their active sessions
                immediately.
              </Alert>
            )}
            {dialogError && <Alert severity="error">{dialogError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              saving ||
              !form.email ||
              (dialog?.mode === 'create' && !form.password)
            }
            onClick={handleSave}
          >
            {saving
              ? 'Saving…'
              : dialog?.mode === 'create'
                ? 'Create Demo User'
                : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default DemoUsers
